import '@babel/polyfill';
import 'array-flat-polyfill';

import React, { memo, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import { GlobalMessage } from '@alycecom/services';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material';
import { ConnectedRouter } from 'connected-react-router';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import { Helmet } from 'react-helmet';

import './i18n/validationLocale';

import { configureStore, history } from './store/configureStore';
import { useCheckBrandingToken } from './hooks/useCustomStyling';
import { theme } from './styles/alyceTheme';
import GlobalStyles from './styles/alyceGlobalStyles';
import Redirect from './components/Shared/Redirect';
import ReadOnlyMode from './components/ReadOnlyMode';
import Routes from './Routes';
import Loadable from './components/Shared/Loadable';
import { getBranding } from './modules/RecipientModule';
import { useCheckCampaignPreviewModeToken, useGetCampaignName } from './hooks/useCampaignPreviewMode';

const BrandingModule = Loadable(() => import('./modules/BrandingModule'));

const store = configureStore();

if (window.APP_CONFIG.environment !== 'production') {
  // eslint-disable-next-line global-require
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React);
}

const App = memo(() => {
  const customStyling = useSelector(getBranding);
  const computedTheme = useMemo(
    () =>
      createTheme({
        ...theme,
        palette: {
          ...theme.palette,
          customStyling: {
            backgroundColor: customStyling.backgroundColor,
            primaryColor: customStyling.primaryColor,
            secondaryColor: customStyling.secondaryColor,
            tertiaryColor: customStyling.tertiaryColor,
            highlightColor: customStyling.highlightColor,
            headlineColor: customStyling.headlineColor,
            primaryCtaColor: customStyling.primaryCtaColor,
            secondaryCtaColor: customStyling.secondaryCtaColor,
          },
        },
      }),
    [customStyling],
  );

  // Check using branding from token
  const isBrandingTokenValid = useCheckBrandingToken();
  const isCampaignPreview = useCheckCampaignPreviewModeToken();
  const campaignName = useGetCampaignName();

  return (
    <ConnectedRouter history={history}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={computedTheme}>
          <>
            <Helmet>
              <title>A gift you’re sure to love</title>
              <meta name="description" content="A gift is waiting for you" />
              <meta property="og:title" content="A gift you’re sure to love" />
              <meta property="og:description" content="A gift is waiting for you " />
            </Helmet>
            <GlobalStyles />
            <Redirect />
            <ReadOnlyMode isCampaignPreview={isCampaignPreview} campaignName={campaignName} />
            <Routes />
            <GlobalMessage.Snackbar />
            {isBrandingTokenValid && <BrandingModule />}
          </>
        </ThemeProvider>
      </StyledEngineProvider>
    </ConnectedRouter>
  );
});

const persistor = persistStore(store);

const render = () => {
  ReactDOM.render(
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <App />
      </PersistGate>
    </Provider>,
    document.getElementById('alyce-recipient'),
  );
};

render();

if (module.hot) {
  module.hot.accept('./index', () => {
    render();
  });
}
